'use client';

import { isNil } from 'es-toolkit';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';

import { jsonParse } from '@savvy/utils/error-handling/json-parse';

export type ClientsTableColumn =
  | 'Household'
  | 'Billable Assets'
  | 'ARR'
  | 'Service Calendar'
  | 'Next Service Item'
  | 'Last Contact'
  | 'Primary'
  | 'Owner'
  | 'Tags'
  | 'Comments';

export const DEFAULT_CLIENTS_TABLE_COLUMNS: readonly ClientsTableColumn[] = [
  'Household',
  'Billable Assets',
  'ARR',
  'Service Calendar',
  'Last Contact',
] as const;

type Availability = {
  readonly includePrimary: boolean;
  readonly includeOwner: boolean;
  readonly includeArr?: boolean;
  readonly includeNextServiceItem?: boolean;
};

const STORAGE_KEY = LOCAL_STORAGE_KEYS.DEFAULT_CLIENTS_COLUMNS;

export function getAvailableClientColumns({
  includePrimary,
  includeOwner,
  includeArr = true,
  includeNextServiceItem = false,
}: Availability): readonly ClientsTableColumn[] {
  const base: ClientsTableColumn[] = ['Household', 'Billable Assets'];
  if (includeArr) base.push('ARR');
  base.push('Service Calendar');
  if (includeNextServiceItem) base.push('Next Service Item');
  base.push('Last Contact', 'Comments', 'Tags');
  if (includePrimary) base.push('Primary');
  if (includeOwner) base.push('Owner');
  return base;
}

export function getDefaultClientColumns(availability: Availability): ClientsTableColumn[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  const allowed = new Set(getAvailableClientColumns(availability));
  if (isNil(stored)) {
    // Add Owner to defaults if available (admin users)
    const defaults = [...DEFAULT_CLIENTS_TABLE_COLUMNS];
    if (availability.includeOwner) {
      defaults.push('Owner');
    }
    return defaults.filter((c) => allowed.has(c));
  }

  const parsed = jsonParse(stored);
  if (parsed.isErr()) {
    const defaults = [...DEFAULT_CLIENTS_TABLE_COLUMNS];
    if (availability.includeOwner) {
      defaults.push('Owner');
    }
    return defaults.filter((c) => allowed.has(c));
  }

  const value = parsed.value;
  if (!isValidColumnArray(value)) {
    const defaults = [...DEFAULT_CLIENTS_TABLE_COLUMNS];
    if (availability.includeOwner) {
      defaults.push('Owner');
    }
    return defaults.filter((c) => allowed.has(c));
  }

  return value.filter((c) => allowed.has(c));
}

export function setDefaultClientColumns(columns: ClientsTableColumn[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
}

export const CLIENTS_COLUMN_TO_SLUG: Record<ClientsTableColumn, string> = {
  Household: 'household',
  'Billable Assets': 'aum',
  ARR: 'arr',
  'Service Calendar': 'service',
  'Next Service Item': 'nsi',
  'Last Contact': 'lcd',
  Primary: 'primary',
  Owner: 'owner',
  Tags: 'tags',
  Comments: 'comments',
} as const;

const SLUG_TO_CLIENTS_COLUMN: Record<string, ClientsTableColumn> = {
  household: 'Household',
  aum: 'Billable Assets',
  arr: 'ARR',
  service: 'Service Calendar',
  nsi: 'Next Service Item',
  lcd: 'Last Contact',
  primary: 'Primary',
  owner: 'Owner',
  tags: 'Tags',
  comments: 'Comments',
} as const;

export function clientsColumnsToSlugs(columns: readonly ClientsTableColumn[]): string[] {
  return columns.map((c) => CLIENTS_COLUMN_TO_SLUG[c]);
}

export function clientsColumnsFromSlugs(slugs: readonly string[]): ClientsTableColumn[] {
  return slugs.map((s) => SLUG_TO_CLIENTS_COLUMN[s]).filter((v): v is ClientsTableColumn => !isNil(v));
}

function isValidColumnArray(value: unknown): value is ClientsTableColumn[] {
  if (!Array.isArray(value)) return false;
  return value.every(
    (v) =>
      v === 'Household' ||
      v === 'Billable Assets' ||
      v === 'ARR' ||
      v === 'Service Calendar' ||
      v === 'Next Service Item' ||
      v === 'Last Contact' ||
      v === 'Primary' ||
      v === 'Owner' ||
      v === 'Tags' ||
      v === 'Comments',
  );
}
